import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import BuyerPage from "@/pages/BuyerPage";
import BuyerMaterialsPage from "@/pages/BuyerMaterialsPage";
import BuyerRequirementPage from "@/pages/BuyerRequirementPage";
import SellPage from "@/pages/SellPage";
import SupplierListingPage from "@/pages/SupplierListingPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import ContactUsPage from "@/pages/ContactUsPage";
import OldMaterialPage from "@/pages/OldMaterialPage";
import ThankYouPage from "@/pages/ThankYouPage";
import AboutUsPage from "@/pages/AboutUsPage";
import TermsAndConditionsPage from "@/pages/TermsAndConditionsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import LoginPage from "@/pages/LoginPage";
import BuyerLoginPage from "@/pages/BuyerLoginPage";
import BuyerAccountPage from "@/pages/BuyerAccountPage";
import StaffLoginPage from "@/pages/StaffLoginPage";
import SupplierLoginPage from "@/pages/SupplierLoginPage";
import SupplierDashboard from "@/pages/SupplierDashboard";
import SupplierQuotationBuilder from "@/pages/SupplierQuotationBuilder";
import AdminDashboard from "@/pages/AdminDashboard";
import ChatbotWidget from "@/components/ChatbotWidget";
import ErrorBoundary from "@/components/ErrorBoundary";

// Resets the boundary whenever the route changes so one crashed page doesn't trap the user.
function RoutedErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

function ProtectedRoute({ children, role, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const allowed = roles ?? (role ? [role] : null);
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="overline">Loading...</div></div>;
  if (!user) {
    const returnSearch = location.search || "";
    if (role === "supplier") {
      return <Navigate to={`/supplier/login${returnSearch}`} replace />;
    }
    if (role === "agent") return <Navigate to="/staff/login" replace />;
    if (role === "buyer") {
      const next = encodeURIComponent(`${location.pathname}${location.search}`);
      return <Navigate to={`/account/login?next=${next}`} replace />;
    }
    return <Navigate to="/login" replace />;
  }
  if (allowed && !allowed.includes(user.role)) return <Navigate to="/" />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <div className="relative min-h-screen">
          <RoutedErrorBoundary>
          <Routes>
            <Route path="/" element={<BuyerPage />} />
          <Route path="/materials/:parentSlug" element={<BuyerMaterialsPage />} />
          <Route path="/materials" element={<BuyerMaterialsPage />} />
          <Route path="/requirement" element={<BuyerRequirementPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/sell" element={<SellPage />} />
          <Route path="/suppliers" element={<SupplierListingPage />} />
          <Route path="/old-material" element={<OldMaterialPage />} />
          <Route path="/contact-us" element={<ContactUsPage />} />
          <Route path="/about-us" element={<AboutUsPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/account/login" element={<BuyerLoginPage />} />
          <Route
            path="/account"
            element={
              <ProtectedRoute role="buyer">
                <BuyerAccountPage />
              </ProtectedRoute>
            }
          />
          <Route path="/staff/login" element={<StaffLoginPage />} />
          <Route path="/supplier/login" element={<SupplierLoginPage />} />
          <Route path="/supplier" element={
            <ProtectedRoute role="supplier"><SupplierDashboard /></ProtectedRoute>
          } />
          <Route path="/supplier/quotation/:assignmentId" element={
            <ProtectedRoute role="supplier"><SupplierQuotationBuilder /></ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>
          } />
          <Route path="/agent" element={
            <ProtectedRoute role="agent"><AdminDashboard /></ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
          </RoutedErrorBoundary>
          <ChatbotWidget />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
